import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import axiosInstance from '../configs/axios';

interface CheckoutStripe {
  gateway_id: number;
  plan_id: number;
  installments: number;
  currency_id: number;
  coupon?: string;
  ref?: string;
  cc_token: string;
  user_doc: string;
  user_phone: string;
  credit_card: string;
  payment_method: string;
  boleto: string;
  squad_type_id?: number | null;
}

interface CheckoutStripeExternal {
  gateway_id: number;
  plan_id: number;
  click_id?: string;
  coupon: string;
  ref: string;
  squad_type_id?: number | null;
}

interface CheckoutStripeElements {
  plan_id: number;
  gateway_id: number;
  coupon: string;
  user_name: string;
  user_email: string;
  click_id?: string;
  squad_type_id?: number | null;
}

export interface CheckoutPIXStripe {
  plan_id: number;
  gateway_id: number;
  coupon: string;
  click_id?: string;
  payment_method: string;
  user_doc: string;
  squad_type_id?: number | null;
}

interface CheckoutStripeElementsWithSignal extends CheckoutStripeElements {
  signal?: AbortSignal;
}

export const checkoutStripe = createAsyncThunk('appCheckoutStripe/checkoutStripe', async (data: CheckoutStripe) => {
  try {
    const response = await axiosInstance.post('/checkout', data);

    return { status: response.status, ...response.data };
  } catch (error) {
    // @ts-ignore
    return { success: false, error: error?.response?.data, status: error?.response?.status };
  }
});

export const checkoutPixStripe = createAsyncThunk('appCheckoutStripe/checkoutPixStripe', async (data: CheckoutPIXStripe) => {
  try {
    const response = await axiosInstance.post('/checkout', data);

    return { status: response.status, data: response.data };
  } catch (error) {
    // @ts-ignore
    return { success: false, error: error?.response?.data, status: error?.response?.status };
  }
});

export const checkoutStripeExternal = createAsyncThunk('appCheckoutStripe/checkoutStripeExternal', async (data: CheckoutStripeExternal) => {
  try {
    const response = await axiosInstance.post('/checkout', data);

    // Mesmo motivo do `checkoutStripeElements` abaixo: este thunk entrega `data.data`, e o redirect
    // externo responde com o visitante ainda na tela — dá tempo de avisar.
    return { status: response.status, data: response.data.data, campaign_discount_applied: response.data?.campaign_discount_applied, campaign_discount_refused_reason: response.data?.campaign_discount_refused_reason };
  } catch (error) {
    // @ts-ignore
    return { success: false, error: error?.response?.data, status: error?.response?.status };
  }
});

export const checkoutStripeExternalTrial = createAsyncThunk('appCheckoutStripe/checkoutStripeExternalTrial', async (data: CheckoutStripeExternal) => {
  try {
    const response = await axiosInstance.post('/checkout/trial', data);

    return { status: response.status, data: response.data.data, campaign_discount_applied: response.data?.campaign_discount_applied, campaign_discount_refused_reason: response.data?.campaign_discount_refused_reason };
  } catch (error) {
    // @ts-ignore
    return { success: false, error: error?.response?.data, status: error?.response?.status };
  }
});

export const checkoutStripeElements = createAsyncThunk('appCheckoutStripe/checkoutStripeElements', async (data: CheckoutStripeElementsWithSignal) => {
  try {
    const { signal, ...requestData } = data;
    const response = await axiosInstance.post('/checkout', requestData, {
      signal,
    });

    // The two campaign fields are carried explicitly because this thunk hands its caller
    // `data.data`, so anything top-level in the body would be dropped — and this is the checkout
    // call that happens BEFORE the visitor clicks Apple/Google Pay, which makes it the one place a
    // refused campaign discount can still be shown in time.
    return { status: response.status, data: response.data.data, success: true, campaign_discount_applied: response.data?.campaign_discount_applied, campaign_discount_refused_reason: response.data?.campaign_discount_refused_reason };
  } catch (error) {
    // @ts-ignore
    if (error?.name === 'AbortError') {
      return { success: false, error: { message: 'Request cancelled' }, aborted: true };
    }
    // @ts-ignore
    return { success: false, error: error?.response?.data, status: error?.response?.status };
  }
});

export const checkoutStripeTrial = createAsyncThunk('appCheckoutStripe/checkoutStripeTrial', async (data: CheckoutStripe) => {
  try {
    const response = await axiosInstance.post('/checkout/trial', data);

    return { status: response.status, data: response.data };
  } catch (error) {
    // @ts-ignore
    return { success: false, error: error?.response?.data, status: error?.response?.status };
  }
});

export const appCheckoutStripeSlice = createSlice({
  name: 'appCheckoutStripe',
  initialState: {
    data: [],
  },
  reducers: {},
});

export default appCheckoutStripeSlice.reducer;
